// 텍스트 생성 관련 서비스 함수들 / Text generation service functions
import { apiClient } from '../api-client';

/**
 * 파일 업로드 서비스 / File upload service
 * @param file 업로드할 파일 / File to upload
 * @returns 업로드된 파일의 URL / Uploaded file URL
 */
export const uploadFile = async (file: File): Promise<string> => {
  try {
    // V2 API (Gemini/Imagen) 사용 / Use V2 API (Gemini/Imagen)
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'https://ailic.kpc.or.kr';
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${baseUrl}/api/v2/kpc/file-upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data?.success && data?.data?.url) {
      return data.data.url;
    } else {
      throw new Error(data?.message || '파일 업로드 실패');
    }
  } catch (error) {
    console.error('파일 업로드 오류 / File upload error:', error);
    throw new Error('파일 업로드 중 오류가 발생했습니다.');
  }
};

/**
 * 여러 파일 업로드 / Upload multiple files
 * @param files 업로드할 파일 배열 / Array of files to upload
 * @returns 업로드된 파일 URL 배열 / Array of uploaded file URLs
 */
export const uploadFiles = async (files: File[]): Promise<string[]> => {
  try {
    const uploadPromises = files.map(file => uploadFile(file));
    const urls = await Promise.all(uploadPromises);
    return urls;
  } catch (error) {
    console.error('다중 파일 업로드 오류 / Multiple file upload error:', error);
    throw new Error('파일 업로드 중 오류가 발생했습니다.');
  }
};

/**
 * 텍스트 생성 (일반 요청) / Text generation (regular request)
 * @param prompt 텍스트 생성 프롬프트 / Text generation prompt
 * @param imageUrls 이미지 URL 배열 (선택사항) / Image URLs array (optional)
 * @returns 생성된 텍스트 / Generated text
 */
export const generateText = async (
  prompt: string,
  imageUrls: string[] = []
): Promise<{
  success: boolean;
  message: string;
  data: {
    generatedText?: string;
    processedImageUrls?: string[];
  };
}> => {
  try {
    const response = await apiClient.api.kpcControllerGenerateText({
      prompt,
      imageUrls,
    });

    return {
      success: response.data?.success ?? false,
      message: response.data?.message ?? 'Unknown error',
      data: {
        generatedText: response.data?.data?.generatedText,
        processedImageUrls: response.data?.data?.processedImageUrls,
      },
    };
  } catch (error) {
    console.error('텍스트 생성 오류 / Text generation error:', error);
    throw new Error('텍스트 생성 중 오류가 발생했습니다.');
  }
};

/**
 * S3 연결 테스트 / Test S3 connection
 * @returns 연결 테스트 결과 / Connection test result
 */
export const testS3Connection = async (): Promise<{
  success: boolean;
  message: string;
  data: {
    success: boolean;
    message: string;
    details: object;
  };
}> => {
  try {
    const response = await apiClient.api.kpcControllerTestS3Connection();
    return {
      success: response.data?.success ?? false,
      message: response.data?.message ?? 'Unknown error',
      data: {
        success: response.data?.data?.success ?? false,
        message: response.data?.data?.message ?? 'Unknown error',
        details: response.data?.data?.details ?? {},
      },
    };
  } catch (error) {
    console.error('S3 연결 테스트 오류 / S3 connection test error:', error);
    throw new Error('S3 연결 테스트 중 오류가 발생했습니다.');
  }
};

// 파일 유효성 검사 / File validation
export const validateFile = (file: File): { isValid: boolean; error?: string } => {
  // 지원되는 파일 타입 / Supported file types
  const supportedTypes = [
    'text/plain',
    'text/csv',
    'application/json',
    'application/xml',
    'image/png',
    'image/jpeg',
    'image/jpg',
  ];

  // 지원되는 파일 확장자 / Supported file extensions
  const supportedExtensions = ['.txt', '.csv', '.json', '.xml', '.png', '.jpeg', '.jpg'];

  // 파일 타입 검사 / Check file type
  if (!supportedTypes.includes(file.type)) {
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!supportedExtensions.includes(extension)) {
      return {
        isValid: false,
        error: '지원되지 않는 파일 형식입니다. (TXT, CSV, JSON, XML, PNG, JPEG, JPG만 지원)',
      };
    }
  }

  // 파일 크기 검사 (2MB 제한) / Check file size (2MB limit)
  const maxSize = 2 * 1024 * 1024; // 2MB
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: '파일 크기가 너무 큽니다. (최대 2MB)',
    };
  }

  return { isValid: true };
};

// 파일 타입별 아이콘 반환 / Return icon for file type
export const getFileIcon = (fileName: string): string => {
  if (!fileName) return '📎';
  
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  
  if (['.png', '.jpeg', '.jpg'].includes(extension)) {
    return '🖼️';
  } else if (extension === '.txt') {
    return '📄';
  } else if (extension === '.csv') {
    return '📊';
  } else if (extension === '.json') {
    return '📋';
  } else if (extension === '.xml') {
    return '📄';
  } else {
    return '📎';
  }
};

// 파일 크기 포맷팅 / Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export { apiClient };
